import * as _ from 'lodash';
import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import {
  Icon,
  icons,
  Menu,
  MenuDivider,
  MenuGroup,
  MenuHeader,
  MenuItem,
  SearchMenuItem,
} from '../../../generic';

import BranchesDropdownTabs from './BranchesDropdownTabs';
import BranchItem from './BranchItem';
import TagItem from './TagItem';
import TrackDefaultBranchCTA from './TrackDefaultBranchCTA';
import type { Tab } from './BranchesDropdownTabs';
import type { Branch, Reference, Tag } from './types';

import styles from './BranchesDropdown.module.less';

type Props = {
  isUserAuthenticated: boolean;
  branches: Branch[];
  tags: Tag[];
  defaultBranch: string;
  onTrackDefaultBranch: () => void;
  onClick: (reference: Reference) => void;
  onArchiveBranch: (branch: Branch) => void;
  onUnarchiveBranch: (branch: Branch) => void;
  onTrackBranch: (branch: Branch) => void;
  onUntrackBranch: (branch: Branch) => void;
};

function referenceNameIncludes(ref: Reference, search: string): boolean {
  return ref.name.toLowerCase().includes(search.toLowerCase());
}

/**
 * This component renders the dropdown button and menu for displaying a project's
 * branches and tags.
 */
const BranchesDropdown: VoidFunctionComponent<Props> = ({
  isUserAuthenticated,
  branches,
  tags,
  defaultBranch,
  onTrackDefaultBranch,
  onClick,
  onArchiveBranch,
  onUnarchiveBranch,
  onTrackBranch,
  onUntrackBranch,
}) => {
  const selectedReference = [...branches, ...tags].find((ref) => ref.isSelected);

  const [selectedTab, selectTab] = useState<Tab>('branches');
  const shouldShowTags = tags.length > 0;

  // Filter all branches and tags by the current search.
  const [search, setSearch] = useState('');
  const filteredBranches = branches.filter((branch) => referenceNameIncludes(branch, search));
  const filteredTags = tags.filter((tag) => referenceNameIncludes(tag, search));

  // Paritition branches into tracked vs untracked.
  const [trackedBranches, unsortedUntrackedBranches] = _.partition(filteredBranches, (branch) => branch.isTracked);

  // We need to know how many tracked branches exist _before_ filtering
  // to decide if we should show the TrackDefaultBranchCTA.
  const unfilteredTrackedBranches = branches.filter((branch) => branch.isTracked);
  const shouldShowTrackDefaultBranchCTA = unfilteredTrackedBranches.length === 0;

  // Ensure that archived branches always appear at the end of the list.
  const untrackedBranches = _.sortBy(unsortedUntrackedBranches, (branch) => branch.isArchived);

  const shouldShowTrackedBranches = trackedBranches.length > 0;
  const shouldShowUntrackedBranches = untrackedBranches.length > 0;
  const shouldShowBranchesDivider = shouldShowTrackedBranches && shouldShowUntrackedBranches;

  return (
    <Menu
      menuClassName={styles.dropdownMenu}
      direction="bottom"
      menuButton={(
        <div className={styles.dropdownMenuButton}>
          <Icon icon={icons.codeBranch} />
          {selectedReference?.name ?? 'Unknown Reference'}
          <Icon icon={icons.chevronDown} />
        </div>
      )}
    >
      <BranchesDropdownTabs
        selectedTab={selectedTab}
        onSelectTab={(tab) => selectTab(tab)}
        shouldShowTags={shouldShowTags}
      />
      <SearchMenuItem
        placeholder="Search"
        onChange={(event) => setSearch(event.target.value)}
        hasBorder
      />
      <MenuDivider />
      <MenuGroup className={styles.dropdownMenuReferences}>
        {selectedTab === 'branches' && (
          <>
            {shouldShowTrackDefaultBranchCTA && (
              <TrackDefaultBranchCTA
                isUserAuthenticated={isUserAuthenticated}
                defaultBranch={defaultBranch}
                onTrackDefaultBranch={onTrackDefaultBranch}
              />
            )}
            {!shouldShowTrackDefaultBranchCTA && filteredBranches.length === 0 && (
              <MenuItem disabled>There are no matching branches.</MenuItem>
            )}
            {shouldShowTrackedBranches && (
              <>
                <MenuHeader>TRACKED</MenuHeader>
                {trackedBranches.map((branch) => (
                  <BranchItem
                    isUserAuthenticated={isUserAuthenticated}
                    branch={branch}
                    isDefault={branch.isDefault}
                    onClick={onClick}
                    onArchiveBranch={onArchiveBranch}
                    onUnarchiveBranch={onUnarchiveBranch}
                    onTrackBranch={onTrackBranch}
                    onUntrackBranch={onUntrackBranch}
                  />
                ))}
              </>
            )}
            {shouldShowBranchesDivider && (
              <MenuDivider />
            )}
            {shouldShowUntrackedBranches && (
              <>
                <MenuHeader>NOT TRACKED</MenuHeader>
                {untrackedBranches.map((branch) => (
                  <BranchItem
                    isUserAuthenticated={isUserAuthenticated}
                    branch={branch}
                    isDefault={branch.isDefault}
                    onClick={onClick}
                    onArchiveBranch={onArchiveBranch}
                    onUnarchiveBranch={onUnarchiveBranch}
                    onTrackBranch={onTrackBranch}
                    onUntrackBranch={onUntrackBranch}
                  />
                ))}
              </>
            )}
          </>
        )}
        {selectedTab === 'tags' && (
          <>
            {filteredTags.length === 0 && (
              <MenuItem disabled>There are no matching tags.</MenuItem>
            )}
            {filteredTags.map((tag) => (
              <TagItem tag={tag} onClick={onClick} />
            ))}
          </>
        )}
      </MenuGroup>
    </Menu>
  );
};

export default BranchesDropdown;
